import React, { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { components } from "react-select";

import { Skeleton, HookSelectFormGroup, Tooltip } from "components";
import { useLoader } from "hooks";
import { AuroraOutcomeStatuses } from "types";

import { ReactComponent as FailIcon } from "static/images/cancel.svg";
import { ReactComponent as SuccessIcon } from "static/images/checked.svg";
import styles from "./auroraOutcomeField.module.scss";

const FIELD_NAME = "aurora_outcome";

const AuroraOutcomeIcon = ({ status }) => {
	switch (status) {
		case AuroraOutcomeStatuses.pass.value:
			return <SuccessIcon className={styles.iconSuccess} />;
		case AuroraOutcomeStatuses.pending_review.value:
			return <span className={styles.iconPending}>!</span>;
		case AuroraOutcomeStatuses.fail.value:
			return <FailIcon className={styles.iconFail} />;
		default:
			return null;
	}
};

type AuroraOutcomeFieldProps = {
	value: string | undefined | null;
	onSubmit: (values: { [key: string]: any }) => any;
	isLoading?: boolean;
	isDisabled?: boolean;
	tooltip?: string;
};

const AuroraOutcomeField = ({
	value,
	onSubmit,
	isDisabled,
	isLoading = false,
	tooltip,
}: AuroraOutcomeFieldProps) => {
	const Loader = useLoader(isLoading);

	const formMethods = useForm();
	const { handleSubmit } = formMethods;

	const defaultValue = useMemo(
		() => (value ? AuroraOutcomeStatuses.getByName(value) : null),
		[value],
	);

	const submitHandler = useCallback(() => {
		handleSubmit(values => onSubmit(values[FIELD_NAME]))();
	}, [handleSubmit, onSubmit]);

	const auroraOutcomeStatuses = AuroraOutcomeStatuses.getOptions();

	return (
		<Loader
			skeleton={<Skeleton variant="text" height="32px" />}
			component={
				<FormProvider {...formMethods}>
					<Tooltip title={tooltip}>
						<div className={styles.checkListDropdownWrapper}>
							<HookSelectFormGroup
								name={FIELD_NAME}
								hideLabel={true}
								options={auroraOutcomeStatuses}
								getOptionValue={option => option.value}
								getOptionLabel={option => option.displayName}
								colSelect={{ sm: 12 }}
								containerClassName={styles.checkListDropdown}
								defaultValue={defaultValue}
								components={{
									Control: ({
										children,
										...controlProps
									}: any) => {
										const { value } =
											controlProps.selectProps;

										return (
											<components.Control
												{...controlProps}
											>
												<AuroraOutcomeIcon
													status={value?.value}
												/>
												{children}
											</components.Control>
										);
									},
								}}
								onChange={submitHandler}
								isDisabled={isDisabled || value === undefined}
								isSearchable={false}
							/>
						</div>
					</Tooltip>
				</FormProvider>
			}
		/>
	);
};

export default AuroraOutcomeField;
